/** List rows. One component per row type named in screens.json `components`. */
import type { ReactNode } from 'react'
import type {
  Account,
  BankConnection,
  CategoryId,
  DebtPlan,
  FeeGroup,
  HealthScorePillar,
  Money,
  Progress,
  RecurringSeries,
  Statement,
  Transaction,
} from '@/generated/types'
import { t, type CopyKey } from '@/lib/copy'
import { formatBasisPoints, formatDate, formatDayMonth, formatMoney, formatMonth, formatPercent } from '@/lib/format'
import {
  accountTypeName,
  cadenceName,
  categoryName,
  connectionStatusName,
  feeKindName,
  milestoneTitle,
} from '@/lib/display'
import { bandColorVar, categoryColorVar, colorVar, type ScoreBand } from '@/lib/tokens'
import { Amount } from './cards'
import { GeneratedText } from './GeneratedText'
import { Badge, Button, Toggle } from './primitives'
import { IconChevron, IconFile } from './icons'
import { TrackBar } from './charts/Bars'
import { Sparkline } from './charts/Sparkline'

export function List({ children }: { children: ReactNode }) {
  return <ul className="list">{children}</ul>
}

export function ListGroupLabel({ children }: { children: ReactNode }) {
  return <li className="list-group-label">{children}</li>
}

function Row({
  onSelect,
  children,
  label,
}: {
  onSelect?: () => void
  children: ReactNode
  label?: string
}) {
  if (!onSelect) {
    return (
      <li>
        <div className="list-row list-row-static">{children}</div>
      </li>
    )
  }
  return (
    <li>
      <button type="button" className="list-row" onClick={onSelect} aria-label={label}>
        {children}
        <IconChevron size={18} />
      </button>
    </li>
  )
}

export function TransactionRow({
  transaction,
  onSelect,
  showAccount,
}: {
  transaction: Transaction
  onSelect?: () => void
  showAccount?: string
}) {
  return (
    <Row onSelect={onSelect} label={transaction.merchant ?? transaction.description}>
      <span className="swatch" style={{ background: categoryColorVar(transaction.category) }} aria-hidden="true" />
      <span className="list-row-main">
        <span className="list-row-title">{transaction.merchant ?? transaction.description}</span>
        <span className="list-row-sub">
          <span>{formatDayMonth(transaction.date)}</span>
          <span>{categoryName(transaction.category)}</span>
          {showAccount ? <span className="table-secondary">{showAccount}</span> : null}
          {transaction.isRecurring ? <Badge>{t('transactions.filter.recurring')}</Badge> : null}
          {transaction.isFee ? <Badge tone="danger">{feeKindName(transaction.feeKind)}</Badge> : null}
        </span>
      </span>
      <span className="list-row-trailing">
        <Amount money={transaction.amount} signed />
      </span>
    </Row>
  )
}

export function AccountRow({ account, onSelect }: { account: Account; onSelect: () => void }) {
  const coverage = account.coverage
  const sub = coverage?.from && coverage.to
    ? t('accounts.row.coverage', { from: formatDate(coverage.from), to: formatDate(coverage.to) })
    : t('accounts.row.noCoverage')

  return (
    <Row onSelect={onSelect} label={account.name}>
      <span className="avatar" aria-hidden="true">
        {account.mask ?? account.name.slice(0, 2).toUpperCase()}
      </span>
      <span className="list-row-main">
        <span className="list-row-title">{account.name}</span>
        <span className="list-row-sub">
          <span>{accountTypeName(account.type)}</span>
          <span>{sub}</span>
          <span className="table-secondary">
            {t('accounts.row.transactions', { count: account.transactionCount ?? 0 })}
          </span>
        </span>
      </span>
      <span className="list-row-trailing">
        <Amount money={account.currentBalance} />
      </span>
    </Row>
  )
}

const CONNECTION_TONE: Record<BankConnection['status'], 'neutral' | 'positive' | 'caution' | 'danger' | 'info'> = {
  active: 'positive',
  syncing: 'info',
  reauthRequired: 'caution',
  error: 'danger',
  disconnected: 'neutral',
}

/**
 * A linked institution. `actions` renders inside the row for connectedAccounts,
 * which needs Reconnect / Sync / Disconnect per row — so a row carrying actions
 * is static rather than a button, to keep buttons out of buttons.
 */
export function InstitutionRow({
  connection,
  onSelect,
  actions,
}: {
  connection: BankConnection
  onSelect?: () => void
  actions?: ReactNode
}) {
  const status = connectionStatusName(connection.status)
  const synced = connection.lastSyncedAt
    ? t('connectedAccounts.row.lastSynced', { date: formatDate(connection.lastSyncedAt) })
    : t('connectedAccounts.row.neverSynced')

  const content = (
    <>
      {/* A provider logo is content and carries the alt text the contract ships
          for it; the initials fallback is decoration and stays hidden, because
          the institution name is already the row title. */}
      {connection.logoUrl ? (
        <span className="avatar">
          <img
            src={connection.logoUrl}
            alt={t('a11y.institutionLogo', { name: connection.institutionName })}
            width={24}
            height={24}
          />
        </span>
      ) : (
        <span className="avatar" aria-hidden="true">
          {connection.institutionName.slice(0, 2).toUpperCase()}
        </span>
      )}
      <span className="list-row-main">
        <span className="list-row-title">{connection.institutionName}</span>
        <span className="list-row-sub">
          <span>{t('connectedAccounts.row.accounts', { count: connection.accountIds.length })}</span>
          <span>{synced}</span>
          {status ? (
            <Badge tone={CONNECTION_TONE[connection.status]}>
              <span className="visually-hidden">{t('a11y.connectionStatus', { status })}</span>
              <span aria-hidden="true">{status}</span>
            </Badge>
          ) : null}
        </span>
      </span>
      {actions ? <span className="list-row-trailing row-actions">{actions}</span> : null}
    </>
  )

  if (actions) {
    return (
      <li>
        <div className="list-row list-row-static">{content}</div>
      </li>
    )
  }
  return (
    <Row onSelect={onSelect} label={connection.institutionName}>
      {content}
    </Row>
  )
}

export function StatementRow({ statement, onSelect }: { statement: Statement; onSelect?: () => void }) {
  return (
    <Row onSelect={onSelect} label={statement.fileName}>
      <span className="avatar" aria-hidden="true">
        <IconFile size={18} />
      </span>
      <span className="list-row-main">
        <span className="list-row-title">
          {statement.periodStart && statement.periodEnd
            ? t('accountDetail.statement.row', {
                from: formatDate(statement.periodStart),
                to: formatDate(statement.periodEnd),
              })
            : statement.fileName}
        </span>
        <span className="list-row-sub">
          <span>{statement.fileName}</span>
        </span>
      </span>
      <span className="list-row-trailing caption">
        {t('accounts.row.transactions', { count: statement.transactionCount ?? 0 })}
      </span>
    </Row>
  )
}

export function CategoryRow({
  category,
  amount,
  change,
  trend,
  onSelect,
}: {
  category: CategoryId
  amount: Money
  change: number | null | undefined
  trend?: number[]
  onSelect: () => void
}) {
  const changeLabel =
    change === null || change === undefined || Math.abs(change) < 0.02
      ? t('categories.row.change.flat')
      : change > 0
        ? t('categories.row.change.up', { percent: formatPercent(Math.abs(change)) })
        : t('categories.row.change.down', { percent: formatPercent(Math.abs(change)) })

  return (
    <Row onSelect={onSelect} label={categoryName(category)}>
      <span className="swatch" style={{ background: categoryColorVar(category) }} aria-hidden="true" />
      <span className="list-row-main">
        <span className="list-row-title">{categoryName(category)}</span>
        <span className="list-row-sub">{t('categories.row.vsAverage', { amount: changeLabel })}</span>
      </span>
      {trend && trend.length > 1 ? (
        <Sparkline values={trend} label={categoryName(category)} color={categoryColorVar(category)} />
      ) : null}
      <span className="list-row-trailing">
        <Amount money={amount} absolute colored={false} />
      </span>
    </Row>
  )
}

export function RecurringRow({ series, onSelect }: { series: RecurringSeries; onSelect: () => void }) {
  return (
    <Row onSelect={onSelect} label={series.name}>
      <span className="swatch" style={{ background: categoryColorVar(series.category ?? 'subscriptions') }} aria-hidden="true" />
      <span className="list-row-main">
        <span className="list-row-title">{series.name}</span>
        <span className="list-row-sub">
          <span>{cadenceName(series.cadence)}</span>
          {series.nextExpected ? <span>{t('recurring.row.next', { date: formatDate(series.nextExpected) })}</span> : null}
          {series.status === 'priceIncreased' ? <Badge tone="caution">{t('recurring.badge.priceIncreased')}</Badge> : null}
          {series.status === 'unused' ? <Badge tone="caution">{t('recurring.badge.unused')}</Badge> : null}
          {series.status === 'ended' || series.status === 'cancelledByUser' ? (
            <Badge>{t('recurring.badge.ended')}</Badge>
          ) : null}
          {(series.overlapsWith?.length ?? 0) > 0 ? <Badge tone="info">{t('recurring.badge.duplicate')}</Badge> : null}
        </span>
      </span>
      <span className="list-row-trailing">
        <Amount money={series.typicalAmount} absolute colored={false} />
        <span className="caption">{formatMoney(series.annualCost, { absolute: true })}</span>
      </span>
    </Row>
  )
}

export function FeeRow({
  fee,
  onSelect,
  onAccountSelect,
  onTransactionSelect,
}: {
  fee: FeeGroup
  onSelect: () => void
  onAccountSelect?: () => void
  onTransactionSelect?: () => void
}) {
  return (
    <li>
      <div className="list-row list-row-static fee-row">
        <span className="swatch" style={{ background: categoryColorVar('fees') }} aria-hidden="true" />
        <span className="list-row-main">
          <span className="list-row-title">{feeKindName(fee.kind)}</span>
          <span className="list-row-sub">
            <span>{t('fees.row.count', { count: fee.count })}</span>
            {fee.avoidable ? <Badge tone="caution">{t('fees.metric.avoidable')}</Badge> : null}
          </span>
        </span>
        <span className="list-row-trailing fee-row-trailing">
          <Amount money={fee.total} absolute colored={false} />
          <span className="row-actions fee-row-actions">
            <Button variant="quiet" onClick={onSelect}>{t('transactions.title')}</Button>
            {onAccountSelect ? (
              <Button variant="quiet" onClick={onAccountSelect}>{t('accounts.title')}</Button>
            ) : null}
            {onTransactionSelect ? (
              <Button variant="quiet" onClick={onTransactionSelect}>{t('transactionDetail.title')}</Button>
            ) : null}
          </span>
        </span>
      </div>
    </li>
  )
}

export function DebtRow({
  account,
  onSelect,
}: {
  account: Account
  onSelect: () => void
}) {
  const balance = Math.abs(account.currentBalance?.amount ?? 0)
  const limit = account.creditLimit?.amount ?? 0
  const utilization = limit > 0 ? balance / limit : null

  return (
    <Row onSelect={onSelect} label={account.name}>
      <span className="list-row-main">
        <span className="list-row-title">{account.name}</span>
        <span className="list-row-sub">
          <span>
            {account.aprBasisPoints
              ? t('debt.row.apr', { rate: formatBasisPoints(account.aprBasisPoints) })
              : t('debt.row.aprUnknown')}
          </span>
          {account.minimumPayment ? (
            <span>{t('debt.row.minimum', { amount: formatMoney(account.minimumPayment, { absolute: true }) })}</span>
          ) : null}
        </span>
        {utilization !== null ? (
          <TrackBar
            ratio={utilization}
            color={utilization > 0.3 ? colorVar('caution') : colorVar('primary')}
            label={`${account.name} ${formatPercent(utilization)}`}
          />
        ) : null}
      </span>
      <span className="list-row-trailing">
        <Amount money={{ amount: balance, currency: account.currency }} colored={false} />
        {utilization !== null ? <span className="caption">{formatPercent(utilization)}</span> : null}
      </span>
    </Row>
  )
}

export function PayoffRow({ debt }: { debt: NonNullable<DebtPlan['debts']>[number] }) {
  return (
    <Row>
      <span className="avatar" aria-hidden="true">
        {debt.order}
      </span>
      <span className="list-row-main">
        <span className="list-row-title">{debt.name ?? ''}</span>
        <span className="list-row-sub">
          {debt.payoffDate ? <span>{t('debtPlanner.row.paidOff', { date: formatDate(debt.payoffDate) })}</span> : null}
          {debt.aprBasisPoints ? <span>{t('debt.row.apr', { rate: formatBasisPoints(debt.aprBasisPoints) })}</span> : null}
        </span>
      </span>
      <span className="list-row-trailing">
        <Amount money={debt.balance} colored={false} />
        {debt.interestPaid ? (
          <span className="caption">{formatMoney(debt.interestPaid, { absolute: true })}</span>
        ) : null}
      </span>
    </Row>
  )
}

export function MonthRow({
  month,
  income,
  spending,
  net,
  complete,
  onSelect,
}: {
  month: string
  income: Money
  spending: Money
  net: Money
  complete: boolean
  onSelect?: () => void
}) {
  return (
    <Row onSelect={onSelect} label={formatMonth(month)}>
      <span className={`list-row-main${complete ? '' : ' muted'}`}>
        <span className="list-row-title">{formatMonth(month)}</span>
        <span className="list-row-sub">
          <span>
            {t('cashFlow.legend.income')} {formatMoney(income, { absolute: true })}
          </span>
          <span>
            {t('cashFlow.legend.spending')} {formatMoney(spending, { absolute: true })}
          </span>
        </span>
      </span>
      <span className="list-row-trailing">
        <Amount money={net} signed />
      </span>
    </Row>
  )
}

export function PillarRow({
  pillar,
  onSelect,
}: {
  pillar: HealthScorePillar
  onSelect: () => void
}) {
  return (
    <Row onSelect={onSelect} label={t(`healthScore.pillar.${pillar.id}` as never)}>
      <span className="list-row-main">
        <span className="list-row-title">{t(`healthScore.pillar.${pillar.id}` as never)}</span>
        <span className="list-row-sub">
          {t('healthScore.pillar.weight', { percent: formatPercent(pillar.weight) })}
        </span>
        {/* One model-authored line about this pillar's own score, or the keyed
            gloss. `span` because this row's content lives inside a button. */}
        <GeneratedText
          text={pillar.narrative}
          copyKey={`healthScore.pillar.${pillar.id}.fallback` as CopyKey}
          as="span"
          className="list-row-sub"
        />
        <TrackBar
          ratio={pillar.score / 100}
          color={bandColorVar(pillar.band as ScoreBand)}
          label={`${t(`healthScore.pillar.${pillar.id}` as never)} ${pillar.score}`}
        />
      </span>
      <span className="list-row-trailing">
        <span className="metric-value">{pillar.score}</span>
      </span>
    </Row>
  )
}

export function MilestoneRow({
  milestone,
  onSelect,
}: {
  milestone: Progress['milestones'][number]
  onSelect?: () => void
}) {
  return (
    <Row onSelect={onSelect} label={milestoneTitle(milestone)}>
      <span className="list-row-main">
        <span className="list-row-title">{milestoneTitle(milestone)}</span>
        <span className="list-row-sub">{formatDate(milestone.date)}</span>
      </span>
      {milestone.impact && milestone.impact.amount !== 0 ? (
        <span className="list-row-trailing">
          <Amount money={milestone.impact} absolute colored={false} />
        </span>
      ) : null}
    </Row>
  )
}

export function SettingRow({
  label,
  value,
  onSelect,
  children,
}: {
  label: string
  value?: string
  onSelect?: () => void
  children?: ReactNode
}) {
  return (
    <Row onSelect={onSelect} label={label}>
      <span className="list-row-main">
        <span className="list-row-title">{label}</span>
      </span>
      {value ? <span className="list-row-trailing body-secondary">{value}</span> : null}
      {children}
    </Row>
  )
}

export function ToggleRow({
  label,
  checked,
  onChange,
}: {
  label: string
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <Row>
      <span className="list-row-main">
        <span className="list-row-title">{label}</span>
      </span>
      <Toggle checked={checked} onChange={onChange} label={label} />
    </Row>
  )
}

export function LinkRow({ label, value, onSelect }: { label: string; value: string; onSelect: () => void }) {
  return (
    <Row onSelect={onSelect} label={label}>
      <span className="list-row-main">
        <span className="list-row-title">{value}</span>
        <span className="list-row-sub">{label}</span>
      </span>
    </Row>
  )
}

export function GoalRow({
  label,
  progressLabel,
  ratio,
}: {
  label: string
  progressLabel: string
  ratio: number
}) {
  return (
    <Row>
      <span className="list-row-main">
        <span className="list-row-title">{label}</span>
        <span className="list-row-sub">{progressLabel}</span>
        <TrackBar ratio={ratio} color={colorVar('primary')} label={progressLabel} />
      </span>
    </Row>
  )
}
